import flatten from '@opten/array/flatten';
import { Component } from '@opten/compiler/metadata/component';
import { ComponentRef } from '@opten/compiler/metadata/di';
import { OnInit } from '@opten/compiler/metadata/lifecycle';
import addClass from '@opten/dom/add-class';
import removeClass from '@opten/dom/remove-class';
import { of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, switchMap } from 'rxjs/operators';
import { distinctUntilUserChanged } from '../../utilities/observable.helper';
import { Location, User } from '../models/customer';
import { Order, RecommendationRenderModel } from '../models/order';
import { ShopCategoryComponent } from '../shop/shop-category';
import { orderService, orderStorage } from '../shop/shop-context';
import hasClass from '@opten/dom/has-class';
import getParentOrSelfWithClass from '@opten/dom/get-parent-or-self-with-class';

@Component({ className: 'js-shop-orders' })
export class OrdersComponent implements OnInit {
	private ordersElement: HTMLElement;
	private orderDetailContent: HTMLElement;
	private orderDetailLoader: HTMLElement;

	private renderModel: {
		ordersOpen?: Order[];
		ordersArchived?: Order[];
		recommandations?: RecommendationRenderModel[];
	} = {};

	@ComponentRef(ShopCategoryComponent) basketComponent: ShopCategoryComponent;

	constructor(protected element: HTMLElement) {
		this.ordersElement = element;
	}

	onInit(): void {
		this.basketComponent.shopContext.onAuthenticationChanged.subscribe(user => this.reinitRendering(user));
		this.orderDetailContent = this.element.querySelector('.js-shop-orders__content') as HTMLElement;
		this.orderDetailLoader = this.element.querySelector('.js-shop-orders__loader') as HTMLElement;

		const type = this.element.getAttribute('data-type');

		this.basketComponent.shopContext.onIsGuest
			.pipe(
				filter(isGuest => isGuest !== null),
				distinctUntilChanged((a, b) => a === b),
				switchMap(isGuest => {
					if (isGuest) {
						window.location.assign(this.element.getAttribute('data-redirect-url'));
						return of(null);
					} else {
						return this.basketComponent.shopContext.onAuthenticationChanged;
					}
				}),
				distinctUntilUserChanged()
			)
			.subscribe(usr => {
				this.reinitRendering(usr, type);
			});

		window.addEventListener('hashchange', () => {
			removeClass(document.querySelector('#shop-orders'), 'collapse--show');
			removeClass(document.querySelector('[data-target="#shop-orders"]'), 'is--active');
			this._updateOrderDetail(type);
		});
	}

	protected reinitRendering(user: User, updateOrders = '') {
		//only getorders if corresponding accordion is opened
		if (updateOrders !== null) {
			switch (updateOrders) {
				case 'open':
					orderService
						.get(user.customerId.toString())
						.pipe(catchError(error => of([])))
						.subscribe(orders => {
							this._updateOpenedOrders(orders, true);
						});
					break;
				case 'archived':
					orderService
						.getArchive(user.customerId.toString())
						.pipe(
							catchError(error => {
								console.error('error loading orders', error);
								return of([]);
							})
						)
						.subscribe(orders => {
							this._updateArchivedOrders(orders, true);
						});
					break;
				case 'recommandations':
					orderStorage.getRecommandations().subscribe(orders => {
						if (orders !== null) {
							// this._updateArchivedOrders(orders);
						} else {
							orderService
								.getRecommandations(user.customerId.toString())
								.pipe(
									catchError(error => {
										console.error('error loading orders', error);
										return of('');
									})
								)
								.subscribe(proposal => {
									this.orderDetailContent.innerHTML = proposal;
									addClass(this.orderDetailLoader, 'd-none');
									this.basketComponent.initAddBulks(false);
								});
						}
					});
					break;
				default:
					this._renderCurrentOrdersModel();
			}
		}
	}

	private _updateNavigation(orders: Order[]) {
		const activeNavigationItems = document.querySelectorAll('.js-shop-orders__navigation[class*="is-active"]');

		activeNavigationItems.forEach(item => {
			const isMobile = hasClass(item, 'js-shop-orders__navigation--mobile');
			item.parentElement.innerHTML += this._ordersNavigationHtml(orders, isMobile);
		});
	}

	private _updateOpenedOrders(orders: Order[], updateModels = false): void {
		this.renderModel.ordersOpen = orders;

		this._updateNavigation(orders);
		this._updateOrderDetail('open');

		if (this.renderModel.ordersOpen !== null && updateModels) {
			this.renderModel.ordersOpen.forEach(order => (order = this._updateOrder(order)));
			orderStorage.updateOpenedOrders(this.renderModel.ordersOpen).subscribe(() => this._renderCurrentOrdersModel());
		} else {
			this._renderCurrentOrdersModel();
		}
	}

	private _updateArchivedOrders(orders: Order[], updateModels = false): void {
		this.renderModel.ordersArchived = orders;

		this._updateNavigation(orders);
		this._updateOrderDetail('archived');

		if (this.renderModel.ordersArchived !== null && updateModels) {
			this.renderModel.ordersArchived.forEach(order => (order = this._updateOrder(order)));
			orderStorage.updateArchivedOrders(this.renderModel.ordersArchived).subscribe(() => this._renderCurrentOrdersModel());
		} else {
			this._renderCurrentOrdersModel();
		}
	}

	private _updateOrder(order: Order): Order {
		if (order != null) {
			order.orderDateFormatted = this._getDateFormatted(order.orderDate);
			order.temporaryDeliveryDateFormatted = this._getDateFormatted(order.temporaryDeliveryDate);
			const products = flatten(order.locationProducts.map(o => o.products));
			order.subtotal = products.reduce((acc, curr) => acc + curr.pricePerPiece, 0);
			order.additionalCosts = order.orderPrice - order.subtotal;
		}
		return order;
	}

	private _getDateFormatted(date: Date): string {
		let formattedDate = '';
		if (date) {
			const newDate = new Date(date);
			const day = newDate.getDate();
			const month = newDate.getMonth() + 1;
			const year = newDate.getFullYear();
			formattedDate = (day > 9 ? day : '0' + day) + '.' + (month > 9 ? month : '0' + month) + '.' + year;
		}
		return formattedDate;
	}

	private _renderCurrentOrdersModel(reinitBasketContext = false): void {
		if (reinitBasketContext) {
			this.ordersElement.addEventListener('finished.opten.renderer', () => this._reinitBasketBulks());
		}
	}

	private _reinitBasketBulks() {
		//reinit addbulks and locations after renderer finished
		this.basketComponent.initAddBulks(true);
		this.ordersElement.removeEventListener('finished.opten.renderer', () => this._reinitBasketBulks());
	}

	private _ordersNavigationHtml(orders: Order[], isMobile: boolean) {
		if (orders && orders.length) {
			const years = OrdersComponent._groupByYear(orders, 'orderDate');
			const items = Object.entries(years)
				.sort((a, b) => b[0].localeCompare(a[0]))
				.map((year: any[], index) => {
					let isOpen = false;
					if (window.location.hash) {
						isOpen = !!year[1].find(o => o.orderNo === window.location.hash.substring(1));
					} else {
						isOpen = index === 0;
					}

					const ords = year[1].map(o => {
						return `<li class="c-sidenav__orders__item">
									<a href="#${o.orderNo}" title="${o.orderNo}">
										<span>${OrdersComponent._formateDate(o.orderDate)}</span>
										<span>${o.orderNo}</span>
									</a>
								</li>`;
					});

					return `<li class="c-sidenav__orders__year">
								<button data-toggle="collapse" href="#${isMobile ? 'm-' : ''}year-${year[0]}" class="${isOpen ? 'is--active' : ''}">
									${year[0]}
									<span class="material-icons">expand_more</span>
								</button>
								<ul id="${isMobile ? 'm-' : ''}year-${year[0]}" data-parent="#${
						isMobile ? 'm-' : ''
					}years" class="c-sidenav__orders__list js-collapse collapse ${isOpen ? 'collapse--show' : ''}">
									${ords.join('')}
								</ul>
							</li>`;
				});

			return `<ul class="c-sidenav__orders" id="${isMobile ? 'm-' : ''}years">${items.join('')}</ul>`;
		}

		return '';
	}

	private _updateOrderDetail(type: string) {
		const orderId = window.location.hash;

		if (orderId) {
			this.orderDetailContent.innerHTML = '';
			removeClass(this.orderDetailLoader, 'd-none');
			document.querySelectorAll('.c-sidenav__orders__item a').forEach(o => removeClass(o, 'is-active'));
			document.querySelectorAll(`[href="${orderId}"]`).forEach(o => addClass(o, 'is-active'));

			orderService
				.getOrderDetail(orderId.substring(1), type)
				.pipe(
					catchError(error => {
						console.error(`error getting order detail for ${orderId}`, error);
						return of(`<p>${this.element.getAttribute('data-error-message')}</p>`);
					})
				)
				.subscribe(result => {
					this.orderDetailContent.innerHTML = result;
					addClass(this.orderDetailLoader, 'd-none');
					this.basketComponent.initAddBulks(false);
				});
		} else {
			const orders = type === 'archived' ? this.renderModel.ordersArchived : this.renderModel.ordersOpen;
			if (orders && orders[0]) {
				window.location.hash = orders[0].orderNo;
			} else {
				const errorMessage = this.element.querySelector('.js-shop-orders__empty');
				if (errorMessage) {
					addClass(this.orderDetailLoader, 'd-none');
					removeClass(errorMessage, 'd-none');
				}
			}
		}
	}

	static _formateDate(date) {
		const d = new Date(date);
		let month = '' + (d.getMonth() + 1);
		let day = '' + d.getDate();

		if (month.length < 2) month = '0' + month;
		if (day.length < 2) day = '0' + day;

		return [day, month].join('.');
	}

	static _groupByYear(array, key) {
		return array.reduce((previous, currentItem) => {
			const group = new Date(currentItem[key]).getFullYear();
			if (!previous[group]) previous[group] = [];
			previous[group].push(currentItem);
			return previous;
		}, {});
	}
}
